"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, TrendingUp, Target, MapPin, Building2, Users, CircleCheck as CheckCircle2, ArrowRight, Sparkles, ChartBar as BarChart3, Compass } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const marketStats = [{
  stat: "68%",
  label: "Local Geo-Intent Searches",
  desc: "Search queries in Stoke-on-Trent explicitly include ST-postcodes or area names like Hanley, Burslem, Fenton, and Newcastle-under-Lyme.",
  icon: Search
}, {
  stat: "3.4x",
  label: "Higher Buyer Conversion",
  desc: "Local Staffordshire commercial search traffic converts at over triple the rate of national traffic due to immediate proximity and trust.",
  icon: TrendingUp
}, {
  stat: "42%",
  label: "SERP Real Estate Open",
  desc: "Over 40% of local page-one search positions in Stoke are currently occupied by outdated national directories or unoptimized competitors.",
  icon: Target
}];
const sectorBehaviors = [{
  id: "b2b-industrial",
  title: "B2B & Industrial Manufacturing",
  location: "Hanley, Fenton & Longton",
  behavior: "Procurement managers search with long-tail commercial intent for local suppliers, engineering contractors, and specialized machinery services.",
  keyQueries: ["Precision engineering Stoke-on-Trent", "Industrial logistics ST4", "Commercial equipment repair Staffordshire"],
  opportunity: "High order values mean capturing even 2-3 extra organic leads per month yields significant ROI."
}, {
  id: "professional-services",
  title: "Professional & Corporate Services",
  location: "Festival Park & Newcastle-under-Lyme",
  behavior: "Clients seek verified expertise nearby, combining Google Map pack reviews with deep technical content before reaching out.",
  keyQueries: ["Commercial solicitor Stoke", "B2B accounting firm Staffordshire", "SEO strategy consultant Hanley"],
  opportunity: "Pairing localized landing pages with structured schema builds immediate local authority over generic national agencies."
}, {
  id: "trades-field-services",
  title: "Commercial Trades & Facilities Management",
  location: "ST1 through ST12 Postcodes",
  behavior: "Mobile-first searches driven by urgent business needs with high click-to-call intent and direct contact requests.",
  keyQueries: ["Commercial electrician Stoke-on-Trent", "HVAC maintenance contractor ST6", "Industrial roofing repair North Staffs"],
  opportunity: "Optimizing Google Business Profiles alongside hyper-local service pages locks in regional market dominance."
}];
const customerBehaviors = [{
  title: "Hyper-Local Trust Signals",
  desc: "Stoke buyers overwhelmingly prefer working with consultants and suppliers who demonstrate authentic local knowledge and clear regional presence."
}, {
  title: "Mobile Commercial Research",
  desc: "Over 61% of local B2B and trade searches take place on mobile devices during working hours, making fast-loading, structured sites critical."
}, {
  title: "Direct Access Over Middlemen",
  desc: "Decisions-makers look for direct specialist contact information rather than agency sales forms or call center gatekeepers."
}];
export default function Stokemarket() {
  const [activeSector, setActiveSector] = useState(sectorBehaviors[0].id);
  const shouldReduceMotion = useReducedMotion();
  const currentSectorData = sectorBehaviors.find(s => s.id === activeSector) || sectorBehaviors[0];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="stokemarketlandscape" data-nav="light" className="relative bg-background py-20 text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 right-0 -z-10 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
      <div className="absolute bottom-0 left-0 -z-10 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground border-accent/30 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider rounded-md">
            <MapPin className="mr-1.5 h-3.5 w-3.5 text-accent" />
            Stoke-on-Trent Market Analysis
          </Badge>

          <h2 className="font-default text-2xl font-bold tracking-tight text-primary sm:text-3xl lg:text-4xl">
            Local Search Landscape &amp; Market Opportunities
          </h2>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            An in-depth analysis of search intent, commercial behavior, and untapped organic growth potential across
            Stoke-on-Trent and Staffordshire businesses.
          </p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {marketStats.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.label} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-md transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-lg">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <span className="font-default text-3xl font-extrabold text-primary">
                        {item.stat}
                      </span>
                    </div>
                    <CardTitle className="pt-3 font-default text-lg font-semibold text-foreground">
                      {item.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {item.desc}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {}
        <div className="mt-16 rounded-2xl border border-border bg-card p-6 shadow-md lg:p-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <Badge variant="outline" className="mb-2 border-accent/40 bg-accent/10 text-accent font-medium rounded-md">
                <Compass className="mr-1 h-3.5 w-3.5" /> Sector Analysis
              </Badge>
              <h3 className="font-default text-2xl font-bold text-primary sm:text-3xl">
                How Stoke-on-Trent Businesses Are Searched
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Select a industry sector to view local query patterns, search behavior, and ranking opportunities.
              </p>
            </div>

            {}
            <div className="mt-6 flex flex-wrap gap-2 lg:mt-0">
              {sectorBehaviors.map((sector, idx) => {
              const isActive = activeSector === sector.id;
              return <Button key={sector.id} variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveSector(sector.id)} data-index={idx} className={`rounded-md transition-colors ${isActive ? "bg-primary text-primary-foreground shadow-sm" : "border-border text-foreground hover:bg-secondary hover:text-secondary-foreground"}`}>
                    {sector.title.split("&")[0].trim()}
                  </Button>;
            })}
            </div>
          </div>

          <Separator className="my-6 bg-border" />

          {}
          <div className="grid gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
                <MapPin className="h-4 w-4" />
                <span>Focus Hub: {currentSectorData.location}</span>
              </div>
              <h4 className="mt-2 font-default text-xl font-bold text-foreground sm:text-2xl">
                {currentSectorData.title}
              </h4>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {currentSectorData.behavior}
              </p>

              <div className="mt-6">
                <h5 className="text-xs font-bold uppercase tracking-wider text-primary">
                  Frequent Local Search Terms:
                </h5>
                <div className="mt-3 flex flex-wrap gap-2">
                  {currentSectorData.keyQueries.map((query, qIdx) => <span key={query} data-index={qIdx} className="inline-flex items-center gap-1.5 rounded-md bg-secondary/80 px-3 py-1.5 text-xs font-medium text-secondary-foreground border border-accent/20">
                      <Search className="h-3 w-3 text-accent" />
                      {query}
                    </span>)}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-background p-6 shadow-sm lg:col-span-5">
              <div className="flex items-center gap-2 font-default text-base font-semibold text-primary">
                <Sparkles className="h-5 w-5 text-accent" />
                <span>The SEO Opportunity</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {currentSectorData.opportunity}
              </p>
              <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Consultant Recommendation</span>
                <span className="text-primary font-semibold">Direct Strategy Required</span>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="mt-16">
          <div className="mb-8 text-center sm:text-left">
            <h3 className="font-default text-2xl font-bold text-primary sm:text-3xl">
              Understanding Local Customer Search Intent
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Core factors driving search decisions across Staffordshire buyers and commercial prospects.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {customerBehaviors.map((item, index) => <div key={item.title} data-index={index} className="flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-primary/30">
                <div>
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary text-accent font-bold text-sm">
                    0{index + 1}
                  </div>
                  <h4 className="mt-4 font-default text-lg font-semibold text-foreground">
                    {item.title}
                  </h4>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {item.desc}
                  </p>
                </div>
                <div className="mt-4 pt-4 border-t border-border flex items-center text-xs font-medium text-primary">
                  <CheckCircle2 className="mr-1.5 h-4 w-4 text-accent" />
                  Verified Stoke Market Insight
                </div>
              </div>)}
          </div>
        </div>

        {}
        <div className="mt-16 rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 shadow-xl relative overflow-hidden">
          <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none translate-x-10 translate-y-10">
            <BarChart3 className="h-96 w-96 text-primary-foreground" />
          </div>

          <div className="relative z-10 max-w-3xl">
            <Badge variant="outline" className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground px-3 py-1 text-xs font-semibold rounded-md">
              Direct Consultant Insight
            </Badge>

            <h3 className="mt-4 font-default text-2xl font-bold sm:text-3xl text-primary-foreground">
              {"\""}Stoke-on-Trent businesses don{"'"}t need generic agency packages. They need tailored local strategy.{"\""}
            </h3>

            <p className="mt-4 text-sm sm:text-base leading-relaxed opacity-90 text-primary-foreground">
              When I analyze search patterns across Staffordshire, I consistently see businesses losing high-value commercial leads to competitors simply because their site isn{"'"}t aligned with actual local search intent. You work directly with me—Yassine Essaadi—to build a targeted, sustainable search presence that captures genuine local revenue.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-background text-foreground shadow-md hover:bg-secondary hover:text-secondary-foreground font-semibold rounded-md">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="ml-2 h-4 w-4 text-accent" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 font-medium rounded-md">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}